import React, { useState, useEffect } from "react";
import SEO from "../seo";
const GeneralLandingPageSettings = () => {
  const [activeSection, setActiveSection] = useState("account-creation");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "account-creation",
        "contact-information",
        "currency-settings",
        "time-zone",
        "brand-assets",
        "automatic-subdomain",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="General Landing Page Settings – Help Center"
        description="Learn how to set up your xPage account, configure contact info, currency, time zone, brand assets, and automatic subdomain before launching your landing page."
        canonical="https://help.xpage.ai/help/landing-page-account/General%20Landing%20Page%20Settings"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          General Landing Page Settings
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Before launching your landing page, it's important to configure your
          xPage account and add key business details. These settings ensure a
          professional, fully functional landing page experience.
        </p>

        {/* Account Creation Section */}
        <section id="account-creation" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Create an xPage Account
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Visit the xPage account creation page.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter a valid email address and password to start.
                </span>
              </li>
            </ol>

            <div className="bg-system200 rounded-lg p-3 mt-3">
              <h3 className="font-semibold text-systemTitle text-sm mb-2">
                Essential details to add:
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-system600">
                <li>Landing page name and contact information</li>
                <li>Legal business name and company address</li>
                <li>Time zone</li>
                <li>Default currency</li>
                <li>Domain name</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Automatic Subdomain Section */}
        <section id="automatic-subdomain" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Automatic Subdomain
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              When you create a landing page, xPage assigns a unique subdomain
              (e.g., myxpage.shop). This serves as your default login and
              verification domain. You can later replace it with a custom domain
              for a fully branded customer experience.
            </p>
          </div>
        </section>

        {/* Contact Information Section */}
        <section id="contact-information" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Landing Page Name & Contact Information
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Your landing page name appears on every page and in customer
              communications.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your xPage admin and navigate to Settings → General.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Update the landing page name, phone number, and contact email.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Currency Settings Section */}
        <section id="currency-settings" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Select Landing Page Currency
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  In Settings → General, go to Store Defaults → Display
                  Currency.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Change Store Currency.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select your preferred currency from the dropdown.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Changing the
                currency updates only the display unit. Product prices, shipping
                fees, and other amounts remain unchanged—review pricing
                accordingly.
              </p>
            </div>
          </div>
        </section>

        {/* Time Zone Section */}
        <section id="time-zone" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Landing Page Time Zone
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to Settings → General → Store General Settings.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select your time zone from the dropdown.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Brand Assets Section */}
        <section id="brand-assets" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Landing Page Brand Assets
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Set up your logo, brand colors, and social media links to create a
              cohesive brand identity across your landing page, sales channels,
              and apps.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to Settings → Brand Identity.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Add or generate your logo.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Configure primary, secondary, and contrast colors.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Link your social media accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Xpage offers
                AI-assisted logo generation and pre-set brand color options for
                a consistent and professional look.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#account-creation"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "account-creation"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Create Account
              </a>
            </li>

            <li>
              <a
                href="#contact-information"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "contact-information"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Contact Information
              </a>
            </li>
            <li>
              <a
                href="#currency-settings"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "currency-settings"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Currency Settings
              </a>
            </li>
            <li>
              <a
                href="#time-zone"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "time-zone"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Time Zone
              </a>
            </li>
            <li>
              <a
                href="#brand-assets"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "brand-assets"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Brand Assets
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GeneralLandingPageSettings;
